import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Smile, CheckCircle, UserCheck, Shield, Target, Users, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const AboutPage = () => {
  const { language } = useLanguage();
  const t = translations.about;
  
  // Track if mobile view
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const coreValues = [
    { icon: Shield, title: t.valueIntegrity[language], description: t.valueIntegrityDesc[language] },
    { icon: Target, title: t.valueProfessional[language], description: t.valueProfessionalDesc[language] },
    { icon: Users, title: t.valueGrowth[language], description: t.valueGrowthDesc[language] },
    { icon: CheckCircle, title: t.valueResponsibility[language], description: t.valueResponsibilityDesc[language] },
    { icon: Heart, title: t.valueGratitude[language], description: t.valueGratitudeDesc[language] }
  ];

  const coreCulture = [
    { icon: Smile, text: t.culturePolite[language] },
    { icon: CheckCircle, text: t.cultureResponsibility[language] },
    { icon: UserCheck, text: t.cultureSelfManagement[language] }
  ];

  // Language-specific mobile styles
  const isZhMobile = language === 'zh' && isMobile;
  const isEnMobile = language === 'en' && isMobile;

  return (
    <div className="pt-16 md:pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className={`bg-gradient-to-b from-primary/5 to-white ${
        isZhMobile ? 'py-6' : isEnMobile ? 'py-5 px-1' : 'py-12 md:py-16'
      }`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`text-center max-w-3xl mx-auto ${
              isZhMobile ? 'mb-4' : isEnMobile ? 'mb-4' : 'mb-8'
            }`}
          >
            <h1 className={`font-bold font-noto-serif text-primary ${
              isZhMobile ? 'text-3xl mb-2' : isEnMobile ? 'text-[22px] mb-2' : 'text-4xl md:text-5xl mb-4'
            }`}>
              {t.pageTitle[language]}
            </h1>
            <div className="w-12 h-0.5 bg-secondary mx-auto"></div>
          </motion.div>
          
          {/* About Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-4xl mx-auto"
          >
            {language === 'zh' ? (
              <p className={`text-text-secondary leading-relaxed font-noto-sans text-center ${
                isZhMobile ? 'text-base px-2' : 'text-lg md:text-xl'
              }`}>
                {t.pageSubtitle1[language]}
              </p>
            ) : (
              // English intro - 2 shorter paragraphs
              <div className={isEnMobile ? 'space-y-3' : 'space-y-5'}>
                <p className={`text-text-primary font-noto-sans ${
                  isEnMobile ? 'text-[14px] leading-relaxed' : 'text-lg leading-relaxed'
                }`}>
                  {t.pageSubtitle1[language]}
                </p>
                <p className={`text-text-secondary font-noto-sans ${
                  isEnMobile ? 'text-[14px] leading-relaxed' : 'text-lg leading-relaxed'
                }`}>
                  {t.pageSubtitle2[language]}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Our Mission */}
      <section className={`bg-white ${
        isZhMobile ? 'py-6' : isEnMobile ? 'py-5' : 'py-12 md:py-16'
      }`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`text-center ${
                isZhMobile ? 'mb-5' : isEnMobile ? 'mb-4' : 'mb-8'
              }`}
            >
              <h2 className={`font-bold font-noto-serif text-primary ${
                isZhMobile ? 'text-2xl mb-2' : isEnMobile ? 'text-xl mb-2' : 'text-3xl md:text-4xl mb-4'
              }`}>
                {t.missionTitle[language]}
              </h2>
              <div className="w-12 h-0.5 bg-secondary mx-auto"></div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className={isZhMobile ? 'space-y-4' : isEnMobile ? 'space-y-2.5' : 'space-y-8'}
            >
              {/* Mission cards - 3 cards for English mobile, different layout for Chinese */}
              {language === 'zh' ? (
                <>
                  {/* Main mission card - Chinese */}
                  <div className={`bg-gradient-to-br from-primary/5 to-accent/10 rounded-xl md:rounded-2xl text-center ${
                    isZhMobile ? 'p-4' : 'p-6 md:p-12'
                  }`}>
                    <p className={`text-text-primary font-noto-sans leading-relaxed ${
                      isZhMobile ? 'text-base' : 'text-base md:text-lg lg:text-xl md:whitespace-nowrap'
                    }`}>
                      {t.missionText[language]}
                    </p>
                  </div>

                  {/* Secondary mission cards - Chinese */}
                  <div className={`grid grid-cols-1 md:grid-cols-2 ${isZhMobile ? 'gap-3' : 'gap-6'}`}>
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                      className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow ${
                        isZhMobile ? 'p-4' : 'p-6'
                      }`}
                    >
                      <p className={`text-text-primary font-noto-sans text-center leading-relaxed ${
                        isZhMobile ? 'text-sm' : ''
                      }`}>
                        {t.missionCard1[language]}
                      </p>
                    </motion.div>
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.3 }}
                      className={`bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow ${
                        isZhMobile ? 'p-4' : 'p-6'
                      }`}
                    >
                      <p className={`text-text-primary font-noto-sans text-center leading-relaxed ${
                        isZhMobile ? 'text-sm' : ''
                      }`}>
                        {t.missionCard2[language]}
                      </p>
                    </motion.div>
                  </div>
                </>
              ) : (
                // English - 3 equal mission cards stacked vertically on mobile
                <div className={`flex flex-col ${isEnMobile ? 'gap-2' : 'gap-4 md:gap-6'}`}>
                  {[t.missionText, t.missionCard1, t.missionCard2].map((content, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className={`bg-gradient-to-br from-primary/5 to-accent/10 rounded-lg shadow-sm ${
                        isEnMobile ? 'py-3 px-4' : 'p-5 md:p-6'
                      }`}
                    >
                      <p className={`text-text-primary font-noto-sans text-center ${
                        isEnMobile ? 'text-[13px] leading-relaxed' : 'text-base md:text-lg leading-relaxed'
                      }`}>
                        {content[language]}
                      </p>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values - Chinese Only */}
      {language === 'zh' && (
        <section className={`bg-background-paper ${isZhMobile ? 'py-5' : 'py-12 md:py-16'}`}>
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`text-center ${isZhMobile ? 'mb-4' : 'mb-10'}`}
            >
              <h2 className={`font-semibold font-noto-serif text-primary ${isZhMobile ? 'text-xl mb-1' : 'text-3xl md:text-4xl mb-3'}`}>
                {t.coreValuesTitle[language]}
              </h2>
              <p className={`text-text-secondary font-noto-sans ${isZhMobile ? 'text-xs' : ''}`}>{t.coreValuesSubtitle[language]}</p>
            </motion.div>

            <div className="max-w-6xl mx-auto">
              {/* First Row - 3 items */}
              <div className={`grid grid-cols-1 md:grid-cols-3 ${isZhMobile ? 'gap-2.5 mb-2.5' : 'gap-5 mb-5'}`}>
                {coreValues.slice(0, 3).map((value, index) => {
                  const IconComponent = value.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="bg-white rounded-lg md:rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                      data-testid={`core-value-${index}`}
                    >
                      <div className={`bg-primary/5 border-b border-gray-100 ${isZhMobile ? 'px-3 py-2' : 'px-5 py-4'}`}>
                        <div className={`flex items-center ${isZhMobile ? 'gap-2' : 'gap-3'}`}>
                          <div className={`bg-secondary/10 rounded-lg flex-shrink-0 ${isZhMobile ? 'p-1.5' : 'p-2.5'}`}>
                            <IconComponent className="text-secondary" size={isZhMobile ? 16 : 22} />
                          </div>
                          <h3 className={`font-semibold font-noto-serif text-primary ${isZhMobile ? 'text-sm' : 'text-xl'}`}>{value.title}</h3>
                        </div>
                      </div>
                      <div className={isZhMobile ? 'px-3 py-2.5' : 'px-5 py-4'}>
                        <p className={`text-text-primary font-noto-sans ${isZhMobile ? 'text-xs leading-relaxed' : 'leading-relaxed text-[15px]'}`}>{value.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              
              {/* Second Row - 2 items centered */}
              <div className={`grid grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto ${isZhMobile ? 'gap-2.5' : 'gap-5'}`}>
                {coreValues.slice(3, 5).map((value, index) => {
                  const IconComponent = value.icon;
                  return (
                    <motion.div
                      key={index + 3}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: (index + 3) * 0.1 }}
                      className="bg-white rounded-lg md:rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                      data-testid={`core-value-${index + 3}`}
                    >
                      <div className={`bg-primary/5 border-b border-gray-100 ${isZhMobile ? 'px-3 py-2' : 'px-5 py-4'}`}>
                        <div className={`flex items-center ${isZhMobile ? 'gap-2' : 'gap-3'}`}>
                          <div className={`bg-secondary/10 rounded-lg flex-shrink-0 ${isZhMobile ? 'p-1.5' : 'p-2.5'}`}>
                            <IconComponent className="text-secondary" size={isZhMobile ? 16 : 22} />
                          </div>
                          <h3 className={`font-semibold font-noto-serif text-primary ${isZhMobile ? 'text-sm' : 'text-xl'}`}>{value.title}</h3>
                        </div>
                      </div>
                      <div className={isZhMobile ? 'px-3 py-2.5' : 'px-5 py-4'}>
                        <p className={`text-text-primary font-noto-sans ${isZhMobile ? 'text-xs leading-relaxed' : 'leading-relaxed text-[15px]'}`}>{value.description}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Core Culture / Core Values */}
      <section className={`${language === 'zh' ? 'bg-white' : 'bg-background-paper'} ${
        isZhMobile ? 'py-6' : isEnMobile ? 'py-5' : 'py-12 md:py-16'
      }`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={`text-center ${
              isZhMobile ? 'mb-5' : isEnMobile ? 'mb-4' : 'mb-10'
            }`}
          >
            <h2 className={`font-semibold font-noto-serif text-primary ${
              isZhMobile ? 'text-2xl mb-2' : isEnMobile ? 'text-xl mb-2' : 'text-3xl md:text-4xl mb-4'
            }`}>
              {t.cultureTitle[language]}
            </h2>
            <div className="w-12 h-0.5 bg-secondary mx-auto"></div>
          </motion.div>

          {/* Both English and Chinese mobile: single-column stack, Desktop: 3 columns */}
          <div className={`max-w-4xl mx-auto ${
            isMobile 
              ? 'grid grid-cols-1 gap-3' 
              : 'grid grid-cols-1 md:grid-cols-3 gap-8'
          }`}>
            {coreCulture.map((item, index) => {
              const IconComponent = item.icon;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={`bg-gradient-to-br from-secondary/10 to-accent/20 text-center shadow-sm hover:shadow-md transition-all ${
                    isEnMobile 
                      ? 'py-3.5 px-4 rounded-xl' 
                      : isZhMobile 
                        ? 'p-4 rounded-xl' 
                        : 'p-8 rounded-2xl'
                  }`}
                  data-testid={`core-culture-${index}`}
                >
                  <IconComponent className={`text-secondary mx-auto ${
                    isEnMobile ? 'mb-2' : isZhMobile ? 'mb-2' : 'mb-4'
                  }`} size={isEnMobile ? 28 : isZhMobile ? 36 : 52} />
                  <h3 className={`font-medium font-noto-serif text-primary ${
                    isEnMobile ? 'text-base' : isZhMobile ? 'text-lg' : 'text-2xl'
                  }`}>{item.text}</h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
